import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { AdjacentType, getParentEdgeTypeId, getSimpleBranchingQuery, getZombieEdge, pluralize, TeamRoles } from '@iotv/datamodel-core';
import { ConsumerDao } from '../../data/ConsumerDAO';
import { ComponentMode, Severity } from '../../types';
import { useFlexibleList } from '../factories/Listerlizer/useFlexibleList';
import { TeamPersonViewDefinition } from './viewDefinition';
import { Alert, Button, Stack } from '@mui/material';
import { ActionIcons } from '../../Iconography';
import { useGridApiRef } from '@mui/x-data-grid';
const debug = process.env.REACT_APP_DEBUG && false;
/**
 * 1. Load linkable people
 * 2. Load linkable people into editable people zone
 * 3. Make edits via
 */
export const LinkableTeamPeopleDialog = ({ team, contextUser, contextCustomer, userFunctions, closeDialog }) => {
    const [linkablePeople, setLinkablePeople] = useState(undefined);
    const [validity, setValidity] = useState({ valid: false, reason: undefined });
    const dgApiRef = useGridApiRef();
    useEffect(() => {
        if (!contextUser || !contextCustomer) {
            return;
        }
        ConsumerDao.getLinkableItems({
            contextObject: team,
            contextUser,
            scopeDefiningObjects: [contextCustomer],
            getLinkableItemsQueryDefinition: getSimpleBranchingQuery('Customer', 'Person', AdjacentType.CHILD).dehydrate(),
            targetEdgeParams: {
                objectTypeId: 'Person',
                adjacencyType: AdjacentType.CHILD,
                edgeTypeId: getParentEdgeTypeId('Person', 'Team'),
            },
        }).then((res) => {
            console.log(`Linkable People Res`, res);
            if (res.err || !res.data) {
                console.error('LinkableTeamPeopleDialog', 'err on get linkable people', res.err?.message ?? 'no data');
                userFunctions.setUserMessage({
                    id: 'err_get_linkable_people',
                    content: 'There was an error retrieving linkable people, please try again in a moment.',
                    label: 'Error',
                    type: 'SnackBar',
                    severity: Severity.error,
                });
            }
            else {
                setLinkablePeople((res.data.Items ?? []));
            }
        });
    }, [team, contextUser?.sk, contextCustomer?.sk]);
    const { component, selectedObjects } = useFlexibleList({
        listItems: linkablePeople ?? [],
        loading: linkablePeople === undefined,
        apiRef: dgApiRef,
        // TODO: we should probably select the person when you give them a team role
        disableRowSelectionOnClick: true,
        editOverride: ComponentMode.EDIT,
        columnOverrides: {
            teamRole: {
                field: 'teamRole',
                headerName: 'Team Role',
                valueGetter: (r, p) => {
                    return p.edgeAttributes?.teamRole ?? null;
                },
                // valueParser: (v, row, col, apiRef) => {
                //   setValidity(checkValidity(apiRef.current.getSelectedRows().values().toArray()))
                //   return v
                // },
                valueSetter: (r, p, n) => {
                    console.log(r, p, n);
                    return { ...p, edgeAttributes: { ...(p.edgeAttributes ?? {}), teamRole: r } };
                },
                // preProcessEditCellProps: (params: GridPreProcessEditCellProps) => {
                //   return { ...params.props, error: params.row.edgeAttributes?.teamRole === undefined }
                // },
                type: 'singleSelect',
                valueOptions: TeamRoles,
                editable: true,
                flex: 1,
            },
        },
        checkboxSelection: true,
        viewDefinition: TeamPersonViewDefinition,
        processRowUpdate: (newRow, oldRow, details) => {
            debug && console.log('ProcessRowUpdate details', details);
            setValidity(checkValidity(newRow));
            return newRow;
        },
        onProcessRowUpdateError: (err) => {
            console.error('LinkableTeamPeopleDialog', 'Error on process row update: ', err);
        },
        updatedItemSelectionCallback: (rowIds, details, selected) => {
            console.log(`Updated Item Selection`);
            //setValidity(checkValidity(details.api.getSelectedRows().values().toArray()))
            setValidity(checkValidity(undefined, selected));
        },
    });
    const linkSelectedPeople = () => {
        const linkPeople = selectedObjects;
        const peopleEdges = linkPeople.map((p) => getZombieEdge(team, p, getParentEdgeTypeId('Person', 'Team'), p.edgeAttributes));
        debug && console.log('LinkableTeamPeopleDialog', 'Saving', peopleEdges);
        ConsumerDao.batchPut({ Items: peopleEdges }).then((link_res) => {
            if (link_res.err || !link_res.data) {
                console.error('Err on save team links', link_res.err?.message ?? 'No data');
                userFunctions.setUserMessage({
                    id: 'errsaveteamlinks',
                    content: 'There was an error linking selected people to this team.',
                    label: 'Error linking people',
                    type: 'SnackBar',
                    severity: Severity.error,
                });
            }
            else if (link_res.data) {
                console.log('I have saved');
                userFunctions.addObjectsToState(peopleEdges);
                userFunctions.setUserMessage({
                    id: 'savedteamlinks',
                    content: `Successfully added ${peopleEdges.length} ${pluralize('person', peopleEdges.length)} to ${team.name}.`,
                    label: 'Linked people',
                    type: 'SnackBar',
                    severity: Severity.success,
                });
                if (closeDialog) {
                    closeDialog();
                }
            }
        });
    };
    // The reason for the rigamaroll with the updatedRow param is because I want to check validity on processRowUpdate, but when the fn runs the edit hasn't actually been committed yet. But I DO have the update row. So I use that when trying to tell.
    const checkValidity = (updatedRow, selected) => {
        const selectedPeople = (selected ?? selectedObjects).map((person) => (person.id === updatedRow?.id ? updatedRow : person));
        if (selectedPeople.length === 0) {
            return { valid: false, reason: undefined };
        }
        for (const selectedPerson of selectedPeople) {
            if (selectedPerson.edgeAttributes?.teamRole === undefined) {
                return { valid: false, reason: 'All selected people must have a team role' };
            }
        }
        return { valid: true, reason: undefined };
    };
    return (_jsxs(Stack, { gap: 1, children: [component, _jsxs(Stack, { direction: "row", justifyContent: 'flex-end', gap: 1, children: [validity.reason !== undefined && _jsx(Alert, { severity: "warning", children: validity.reason }), _jsx(Button, { variant: "contained", startIcon: _jsx(ActionIcons.Link, {}), onClick: linkSelectedPeople, disabled: validity.valid !== true, children: "Link" })] })] }));
};
